import React, { useEffect, useMemo, useState } from "react";
import { Chip, IconButton } from "@mui/material";
import { Eye, Pencil, Trash2 } from "lucide-react";
import {
  getRoleAction,
  getRoleByIdAction,
  deleteRoleAction,
} from "../../Actions/RoleActions";
import { getPermissionData, getUserData } from "../../Actions/common";
import AddEditRolesModal from "./AddEditRoles";
import CustomSnackbar from "../Common/snackBar";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import DataTable from "../../components/DataTable/DataTable";
import { CommonTooltip } from "../../utils/tooltip";
import { formatDateTime } from "../Common/DateFormat";
import showConfirmation from "../../utils/Conformation";

const ACTION_ICON_SIZE = 16;

const RoleManagerSection = () => {
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRolesModal, setShowNewRolesModal] = useState(false);
  const [showViewRolesModal, setShowViewRolesModal] = useState(false);
  const [roleDataById, setRoleDataById] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterValues, setFilterValues] = useState({
    search: "",
    type: "all",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "roles"
  );

  const isOwnRole = (roleId) =>
    roleId?.toString() === userData?.roleId?.toString();

  const fetchRoles = () => {
    setLoading(true);
    getRoleAction()
      .then((res) => {
        const { data } = res?.data;
        setRoles(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to fetch roles",
          severity: "error",
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRoles = useMemo(() => {
    const search = filterValues.search.trim().toLowerCase();
    return roles.filter((role) => {
      const matchesSearch =
        !search || role.roleName?.toLowerCase().includes(search);
      const matchesType =
        filterValues.type === "all" ||
        (filterValues.type === "admin" && role.isAdmin) ||
        (filterValues.type === "standard" && !role.isAdmin);
      return matchesSearch && matchesType;
    });
  }, [roles, filterValues]);

  const pagedRoles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRoles.slice(start, start + pageSize);
  }, [filteredRoles, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredRoles.length / pageSize) || 1);
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredRoles.length, pageSize, currentPage]);

  const handleAddEditRole = async (id) => {
    if (id) {
      try {
        const res = await getRoleByIdAction(id);
        setRoleDataById(res?.data?.data);
        setShowViewRolesModal(false);
        setShowNewRolesModal(true);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to load role",
          severity: "error",
        });
      }
      return;
    }
    setRoleDataById(null);
    setShowViewRolesModal(false);
    setShowNewRolesModal(true);
  };

  const handleViewRole = async (id) => {
    try {
      const res = await getRoleByIdAction(id);
      setRoleDataById(res?.data?.data);
      setShowViewRolesModal(true);
      setShowNewRolesModal(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to load role",
        severity: "error",
      });
    }
  };

  const handleCloseModal = () => {
    setShowNewRolesModal(false);
    setShowViewRolesModal(false);
    setRoleDataById(null);
  };

  const handleDelete = async (id) => {
    await showConfirmation("simpleDelete", {
      entityName: "Role",
      entityId: id,
      action: async (roleId) => {
        const res = await deleteRoleAction(roleId);
        if (res?.data?.status === 202) {
          const error = new Error(res.data.message || "Role is already assigned to a user.");
          error.response = { data: { message: res.data.message } };
          throw error;
        }
        return res;
      },
      onSuccess: () => {
        fetchRoles();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Something went wrong",
          severity: "error",
        });
      },
      showCancelledMsg: true,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    {
      field: "roleName",
      headerName: "Role Name",
      minWidth: 220,
      flex: 1.4,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              display: "grid",
              placeItems: "center",
              width: 34,
              height: 34,
              borderRadius: 10,
              background: params.row.isAdmin ? "#eef2ff" : "#f1f5f9",
              color: params.row.isAdmin ? "#22419e" : "#64748b",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.02em",
              flexShrink: 0,
            }}
          >
            {(params.row.roleName || "R").charAt(0).toUpperCase()}
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: "#333",
                lineHeight: 1.3,
              }}
            >
              {params.row.roleName || "-"}
            </div>
            {isOwnRole(params.row.id) && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Your current role
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      field: "isAdmin",
      headerName: "Type",
      minWidth: 130,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.isAdmin ? "Admin" : "Standard"}
          sx={{
            height: 26,
            fontWeight: 600,
            fontSize: 11.5,
            borderRadius: "8px",
            bgcolor: params.row.isAdmin ? "#eef2ff" : "#f1f5f9",
            color: params.row.isAdmin ? "#22419e" : "#475569",
            "& .MuiChip-label": { px: 1.2 },
          }}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created On",
      minWidth: 150,
      flex: 0.9,
      renderCell: (params) => (
        <span style={{ fontSize: 14, color: "#555", fontWeight: 400 }}>
          {formatDateTime(params.row.createdAt)}
        </span>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated On",
      minWidth: 150,
      flex: 0.9,
      renderCell: (params) => (
        <span style={{ fontSize: 14, color: "#555", fontWeight: 400 }}>
          {formatDateTime(params.row.updatedAt)}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 160,
      flex: 0.8,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const own = isOwnRole(params.row.id);
        return (
          <div
            className="dt-action-cell"
            style={{ width: "100%", justifyContent: "center" }}
          >
            {hasPermission?.canView && (
              <CommonTooltip title="View" placement="top" arrow>
                <IconButton
                  className="dt-action-btn dt-action-view"
                  onClick={() => handleViewRole(params.row.id)}
                  size="small"
                >
                  <Eye size={ACTION_ICON_SIZE} strokeWidth={1.9} />
                </IconButton>
              </CommonTooltip>
            )}
            {hasPermission?.canEdit && !own && (
              <CommonTooltip title="Edit" placement="top" arrow>
                <IconButton
                  className="dt-action-btn dt-action-edit"
                  onClick={() => handleAddEditRole(params.row.id)}
                  size="small"
                >
                  <Pencil size={ACTION_ICON_SIZE} strokeWidth={1.9} />
                </IconButton>
              </CommonTooltip>
            )}
            {hasPermission?.canDelete && !own && (
              <CommonTooltip title="Delete" placement="top" arrow>
                <IconButton
                  className="dt-action-btn dt-action-delete"
                  onClick={() => handleDelete(params.row.id)}
                  size="small"
                >
                  <Trash2 size={ACTION_ICON_SIZE} strokeWidth={1.9} />
                </IconButton>
              </CommonTooltip>
            )}
          </div>
        );
      },
    },
  ];

  const filterConfig = [
    {
      type: "search",
      name: "search",
      label: "Search",
      placeholder: "Search roles...",
    },
    {
      type: "select",
      name: "type",
      label: "Role Type",
      defaultValue: "all",
      options: [
        { label: "All Types", value: "all" },
        { label: "Admin", value: "admin" },
        { label: "Standard", value: "standard" },
      ],
    },
  ];

  const mode = roleDataById ? "edit" : "add";

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        columns={columns}
        rows={pagedRoles}
        loading={loading}
        totalCount={filteredRoles.length}
        page={currentPage}
        pageSize={pageSize}
        filters={filterConfig}
        filterValues={filterValues}
        onFilterApply={(values) => {
          setFilterValues(values);
          setCurrentPage(1);
        }}
        onFilterReset={() => {
          setFilterValues({ search: "", type: "all" });
          setCurrentPage(1);
        }}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        addButtonText="ADD ROLE"
        onAddClick={() => handleAddEditRole()}
        hasCreatePermission={hasPermission?.canCreate}
        actions={{}}
      />

      {showNewRolesModal && (
        <AddEditRolesModal
          open={showNewRolesModal}
          handleClose={handleCloseModal}
          roleDataById={roleDataById}
          setSnackbar={setSnackbar}
          mode={mode}
          showViewRolesModal={showViewRolesModal}
          fetchRoles={fetchRoles}
          userData={userData}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default RoleManagerSection;
